import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { MoonStar, Mail, MessageCircle, X } from "lucide-react";

export default function ChiSono() {
  const [content, setContent] = useState(null);
  const [showContact, setShowContact] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-16 sm:py-24" data-testid="chi-sono-page">
      <div className="text-center mb-12">
        <h1 className="font-display text-4xl sm:text-5xl text-amber-100" data-testid="chi-sono-title">Chi Sono</h1>
      </div>
      <div className="mystic-card p-8 sm:p-12 space-y-12">
        <section data-testid="chi-sono-benvenuti">
          <div className="text-center">
            <MoonStar className="w-8 h-8 text-amber-400/60 mx-auto mb-4" />
            <h2 className="font-cinzel text-sm sm:text-base tracking-[0.25em] text-amber-400 uppercase">
              Benvenuti nel mio mondo!
            </h2>
          </div>
          <div className="mt-8 space-y-4 text-purple-100/85 leading-relaxed text-base sm:text-lg" data-testid="chi-sono-text">
            <p>Il mio nome è Virginia. Leggo le carte sin da bambina ed esercito questa professione da oltre trent’anni.</p>
            <p>Nel corso degli anni ho lavorato e lavoro tuttora con diversi mazzi, mezzi e metodi.</p>
            <p>Ma non mi limito a leggere.</p>
            <p className="font-display text-2xl sm:text-3xl text-amber-200 italic py-2">Io ascolto.</p>
            <p>Sono chiarosenziente: mentre parlo con te, percepisco.</p>
            <p>Sento quando qualcosa non torna, quando c’è confusione, quando una verità viene trattenuta.</p>
            <p>Le carte mi danno una struttura.</p>
            <p>Ma quello che arriva davvero… lo sento.</p>
            <p>Ed è da lì che nasce la mia accurata previsione.</p>
            <p className="pt-2">Non dico quello che volete sentirvi dire.</p>
            <p className="font-display text-2xl sm:text-3xl text-amber-200 italic py-2">Vi dico quello che c’è.</p>
            <p>Perché non si tratta di indovinare.</p>
            <p>Si tratta di capire davvero.</p>
            <p className="pt-2">Se cercate illusioni, mi dispiace, ma non sono la persona giusta per voi.</p>
            <p>Se volete vedere chiaro, allora sì.</p>
          </div>
        </section>

        <div className="h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
          <section data-testid="chi-sono-mazzi">
            <h2 className="font-cinzel text-xs sm:text-sm tracking-[0.25em] text-amber-400 uppercase mb-5">
              Tra i mazzi che leggo
            </h2>
            <ul className="space-y-2.5">
              {["Sibille", "Lenormand", "Kipperkarten", "Tarocchi", "Napoletane", "Stesa a 26 carte Napoletane", "Piacentine", "Poker", "E altro..."].map((item) => (
                <li key={item} className="flex items-center gap-3 text-purple-100/85 text-sm sm:text-base">
                  <span className="w-1.5 h-1.5 rotate-45 bg-amber-400/80 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </section>
          <section data-testid="chi-sono-mezzi">
            <h2 className="font-cinzel text-xs sm:text-sm tracking-[0.25em] text-amber-400 uppercase mb-5">
              Tra i mezzi che utilizzo
            </h2>
            <ul className="space-y-2.5">
              {["Rune", "I Ching", "Bibliomanzia", "Caffemanzia", "Geomanzia", "E altro..."].map((item) => (
                <li key={item} className="flex items-center gap-3 text-purple-100/85 text-sm sm:text-base">
                  <span className="w-1.5 h-1.5 rotate-45 bg-fuchsia-400/70 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </section>
        </div>

        <div className="h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />

        <section data-testid="chi-sono-metodi">
          <h2 className="font-cinzel text-xs sm:text-sm tracking-[0.25em] text-amber-400 uppercase mb-5 text-center">
            I miei metodi
          </h2>
          <p className="text-purple-100/85 leading-relaxed text-base sm:text-lg text-center max-w-xl mx-auto">
            Amo sperimentare e mettermi continuamente alla prova. Per questo, nel corso degli anni, ho sviluppato anche alcuni metodi di lettura esclusivamente miei.
          </p>
        </section>

        <div className="h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />

        <section data-testid="chi-sono-arcana">
          <h2 className="font-cinzel text-xs sm:text-sm tracking-[0.25em] text-amber-400 uppercase mb-6 text-center">
            Come funziona Arcana
          </h2>
          <div className="text-purple-100/85 leading-relaxed text-sm sm:text-base space-y-4 max-w-2xl mx-auto">
            <p>
              Arcana non è un'intelligenza artificiale generica: è stata istruita sulla mia metodologia di lettura
              e sui significati della Vera Sibilla che ho maturato in oltre trent'anni di cartomanzia.
            </p>
            <p>
              Quando interpreta una stesa, Arcana segue il mio metodo: considera la domanda, le carte estratte,
              le loro posizioni, il rapporto tra le carte, le sfumature della stesa e il contesto della domanda.
            </p>
            <p>
              Arcana però non sono io e non pretende di sostituirmi: è uno strumento digitale che porta il mio metodo
              di lettura dentro l'app, rendendolo consultabile in autonomia.
            </p>
            <p className="text-amber-200/90 font-medium">
              Virginia è il consulto personale con me. Arcana è il mio metodo di lettura, disponibile nell'app
              attraverso un'intelligenza artificiale istruita su di esso.
            </p>
          </div>
        </section>
        {content?.quote && (
          <blockquote className="mt-10 pt-8 border-t border-amber-500/20 text-center" data-testid="chi-sono-quote">
            <p className="font-display text-xl sm:text-2xl text-amber-200/90 italic">“{content.quote}”</p>
          </blockquote>
        )}
      </div>
      <div className="text-center mt-12">
        <button onClick={() => setShowContact(true)} data-testid="chi-sono-book-btn" className="gold-btn inline-block px-8 py-3 text-sm">
          Contatta Virginia
        </button>
      </div>

      {showContact && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
          onClick={() => setShowContact(false)}
          data-testid="chi-sono-contact-overlay"
        >
          <div className="mystic-card max-w-sm w-full p-8 relative" onClick={(e) => e.stopPropagation()} data-testid="chi-sono-contact-modal">
            <button
              onClick={() => setShowContact(false)}
              data-testid="contact-modal-close"
              className="absolute top-4 right-4 text-purple-300/60 hover:text-amber-300 transition-colors"
              aria-label="Chiudi"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="font-display text-2xl text-amber-100 text-center">Contatta Virginia</h2>
            <p className="text-sm text-purple-200/70 text-center mt-3">Scegli come contattarmi direttamente:</p>
            <div className="mt-7 space-y-3">
              {content?.contact_email && (
                <a
                  href={`mailto:${content.contact_email}`}
                  data-testid="contact-option-email"
                  className="flex items-center gap-4 rounded-xl border border-[#4a2175] hover:border-amber-500/50 bg-[#180a26]/60 px-5 py-4 transition-colors group"
                >
                  <Mail className="w-5 h-5 text-amber-400 shrink-0" />
                  <div>
                    <p className="text-amber-100 text-sm font-medium">Email</p>
                    <p className="text-purple-300/60 text-xs mt-0.5 group-hover:text-purple-200 transition-colors">{content.contact_email}</p>
                  </div>
                </a>
              )}
              {content?.contact_whatsapp && (
                <a
                  href={`https://wa.me/${content.contact_whatsapp.replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-option-whatsapp"
                  className="flex items-center gap-4 rounded-xl border border-[#4a2175] hover:border-amber-500/50 bg-[#180a26]/60 px-5 py-4 transition-colors group"
                >
                  <MessageCircle className="w-5 h-5 text-amber-400 shrink-0" />
                  <div>
                    <p className="text-amber-100 text-sm font-medium">WhatsApp</p>
                    <p className="text-purple-300/60 text-xs mt-0.5 group-hover:text-purple-200 transition-colors">{content.contact_whatsapp}</p>
                  </div>
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
